import { ReceivableRow } from '@bariendo/lib/receivables';
import { Button, Group, Popover, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { ReactElement, useState } from 'react';
import { alert } from '@/services/alert';
import { ORBIT_GRAY } from '@/theme/colors';
import { ReceivableActions } from './useReceivableActions';

/** The pencil on an Invoiced or Paid line (BAR-3104): its invoice, and once paid its payment. */

function parseAmount(value: string): number {
  const amount = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(amount) ? Math.round(amount * 100) / 100 : 0;
}

const asText = (amount: number | null) => (amount === null ? '' : amount.toFixed(2));

export function EditReceivableCell({ row, actions }: { row: ReceivableRow; actions: ReceivableActions }): ReactElement {
  const [opened, setOpened] = useState(false);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={300}
      withinPortal
      shadow="md"
      transitionProps={{ duration: 0 }}
    >
      <Popover.Target>
        <UnstyledButton title="Edit" aria-label="Edit" c={ORBIT_GRAY} onClick={() => setOpened((open) => !open)}>
          <IconPencil size={15} stroke={1.7} />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        {opened && <EditForm row={row} actions={actions} onDone={() => setOpened(false)} />}
      </Popover.Dropdown>
    </Popover>
  );
}

function EditForm({
  row,
  actions,
  onDone,
}: {
  row: ReceivableRow;
  actions: ReceivableActions;
  onDone: () => void;
}): ReactElement {
  const paid = row.status === 'PAID';
  const [invoicedOn, setInvoicedOn] = useState(row.invoicedOn ?? '');
  const [invoicedAmount, setInvoicedAmount] = useState(asText(row.invoicedAmount));
  const [invoiceNumber, setInvoiceNumber] = useState(row.invoiceNumber ?? '');
  const [paidOn, setPaidOn] = useState(row.paidOn ?? '');
  const [paidAmount, setPaidAmount] = useState(asText(row.paidAmount));
  const [bankReference, setBankReference] = useState(row.bankReference ?? '');
  const [saving, setSaving] = useState(false);

  const ready =
    Boolean(invoicedOn) &&
    parseAmount(invoicedAmount) > 0 &&
    Boolean(invoiceNumber.trim()) &&
    (!paid || (Boolean(paidOn) && parseAmount(paidAmount) > 0 && Boolean(bankReference.trim())));

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      await actions.correct(row, {
        invoicedOn,
        invoicedAmount: parseAmount(invoicedAmount),
        invoiceNumber: invoiceNumber.trim(),
        ...(paid ? { paidOn, paidAmount: parseAmount(paidAmount), bankReference: bankReference.trim() } : {}),
      });
      onDone();
    } catch (error) {
      alert.error(`Couldn't save the edit: ${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={8}>
      <Text fz={13} fw={600}>
        {`Edit ${row.invoiceNumber ?? ''}`}
      </Text>
      <TextInput
        label="Invoice date"
        size="xs"
        type="date"
        value={invoicedOn}
        onChange={(event) => setInvoicedOn(event.currentTarget.value)}
      />
      <TextInput
        label="Invoiced amount"
        size="xs"
        inputMode="decimal"
        value={invoicedAmount}
        onChange={(event) => setInvoicedAmount(event.currentTarget.value)}
      />
      <TextInput
        label="Invoice number"
        size="xs"
        value={invoiceNumber}
        onChange={(event) => setInvoiceNumber(event.currentTarget.value)}
      />
      {paid && (
        <>
          <TextInput
            label="Paid date"
            size="xs"
            type="date"
            value={paidOn}
            onChange={(event) => setPaidOn(event.currentTarget.value)}
          />
          <TextInput
            label="Amount received"
            size="xs"
            inputMode="decimal"
            value={paidAmount}
            onChange={(event) => setPaidAmount(event.currentTarget.value)}
          />
          <TextInput
            label="Bank reference ID"
            size="xs"
            value={bankReference}
            onChange={(event) => setBankReference(event.currentTarget.value)}
          />
        </>
      )}
      <Group justify="flex-end" gap={8} mt={4}>
        <Button size="xs" variant="default" onClick={onDone}>
          Cancel
        </Button>
        <Button size="xs" color="dark" loading={saving} disabled={!ready} onClick={() => void save()}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}
